// 📁 src/lightversion/DirectionConfirmLite.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Divider,
} from "@mui/material";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useLite } from "./context/LiteContext";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";
import LiteLearningBlock from "./components/LiteLearningBlock"; // ✅ ADDITION

/* --------------------------------------------------
   🛡️ Constants
-------------------------------------------------- */


const FALLBACK_TEXT =
  "This perspective often becomes clearer as you explore it further.";

const DirectionConfirmLite = () => {
  const navigate = useNavigate();
  const { 
  liteJourney,
  markDayComplete,
  entryDecided,
  loading: liteLoading
  } = useLite();

  const selectedDirection = liteJourney?.direction;
  const purposeStatement = liteJourney?.purpose?.statement;
  const completedDays = liteJourney?.completedDays || [];

  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* =====================================================
     🔒 ENTRY DECISION GUARD
  ===================================================== */
  useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome",{replace:true});
  }

  }, [entryDecided, liteLoading, navigate]);

  /* =====================================================
     🔁 DAY ORDER GUARDS
  ===================================================== */
  useEffect(() => {
    if (!completedDays.includes(1)) {
      navigate("/lite/mentor", { replace: true });
      return;
    }
    if (!completedDays.includes(2)) {
      navigate("/lite/purpose", { replace: true });
      return;
    }
    if (!completedDays.includes(3)) {
      navigate("/lite/idea", { replace: true });
      return;
    }
    if (completedDays.includes(4)) {
      navigate("/lite/market-potential", { replace: true });
    }
  }, [completedDays, navigate]);

  /* =====================================================
     🔁 LOAD DIRECTION INSIGHT
  ===================================================== */
  useEffect(() => {
    if (!selectedDirection || insight || loading) return;

    setLoading(true);
    setError("");

    axios
      .post("/lite/direction/insight", {
        purpose: purposeStatement,
        direction: selectedDirection,
      })
      .then((res) => {
        setInsight(res.data || {});
      })
      .catch(() => {
        setError(
          "We couldn’t generate insights right now. You can still continue."
        );
      })
      .finally(() => setLoading(false));
  }, [selectedDirection, insight, loading, purposeStatement]);

  /* =====================================================
     ✅ COMPLETE DAY 4
  ===================================================== */
  const handleCompleteDay = () => {
    markDayComplete();
    navigate("/lite/market-potential");
  };

  /* =====================================================
     🛑 SAFETY: No direction
  ===================================================== */
  if (!selectedDirection) {
    return (
      <LiteDayLayout day={4}>
        <Alert severity="warning">
          No direction selected. Please explore directions first.
        </Alert>
      </LiteDayLayout>
    );
  }

  return (
    <LiteDayGate allow={["DIRECTION_CONFIRMATION", "INSIGHT_BUILDING"]}>
      <LiteDayLayout
        day={4}
        allowAction={!!insight}
        onComplete={handleCompleteDay}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* HEADER */}
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Sense-check Your Direction
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Today is about understanding what this direction usually involves —
            not locking yourself in.
          </Typography>

          {/* 🔹 DAY 4 INTRO */}
          <Box sx={{ mb: 6 }}>
            <Stack spacing={4}>
              <ExperienceItem
                icon="🧭"
                title="Understand the direction better"
                text="Every path has consequences—know them before you commit.Explore where this direction typically leads.Founders choose wisely when they see the bigger picture."
              />
              <ExperienceItem
                icon="🧠"
                title="See trade-offs clearly"
                text="Every path has strengths—and blind spots.Today is about facing both with honesty.Founders win by choosing eyes wide open."
              />
              <ExperienceItem
                icon="🌱"
                title="Build calm commitment"
                text="Founders don’t need all the answers today.You’re not locking in forever—just choosing a path to explore.Calm conviction beats rushed decisions."
              />
            </Stack>
          </Box>

          {/* 🧠 OPTIONAL LITE LEARNING (DAY 3) */}
          <LiteLearningBlock day={3} />

          {/* 🔹 SELECTED DIRECTION */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Selected direction
              </Typography>

              <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                {selectedDirection.title}
              </Typography>

              <Typography variant="body2" sx={{ mt: 2 }}>
                {selectedDirection.summary}
              </Typography>
            </CardContent>
          </Card>

          {/* 🔹 INSIGHT STATES */}
          {loading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Interpreting this direction…
            </Alert>
          )}

          {error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* 🔹 INSIGHTS */}
          {insight && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography fontWeight="bold">
                      Why founders explore this direction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insight.whyThisDirection || FALLBACK_TEXT}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography fontWeight="bold">
                      What this direction is good for
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insight.whatItIsGoodFor || FALLBACK_TEXT}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography fontWeight="bold">
                      What it won’t solve immediately
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insight.whatItDoesNotSolve || FALLBACK_TEXT}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography fontWeight="bold">
                      Mindset that works best here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insight.mindsetRequired || FALLBACK_TEXT}
                    </Typography>
                  </Box>

                  <Divider />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {insight.reassurance || FALLBACK_TEXT}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* 🔹 CTA */}
          <Box textAlign="center" sx={{ mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleCompleteDay}
              disabled={!insight}
            >
              This direction feels right → Continue
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Next, you’ll explore whether this problem has real-world relevance.
            </Typography>
          </Box>
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default DirectionConfirmLite;
